import { useEffect, useState } from "react";
import { Loader2, TrendingUp, TrendingDown, Minus, Rocket, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { EmptyState } from "@/components/dashboard/ui";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString();

function ChangePill({ pct }) {
  const flat = Math.abs(pct) < 0.5;
  const positive = pct > 0;
  const Icon = flat ? Minus : positive ? TrendingUp : TrendingDown;
  const cls = flat
    ? "text-slate-400 border-white/10 bg-white/[0.02]"
    : positive
    ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10"
    : "text-red-300 border-red-500/30 bg-red-500/10";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      <Icon size={12} /> {flat ? "flat" : `${positive ? "+" : ""}${pct.toFixed(1)}%`}
    </span>
  );
}

export default function SongsOnTheMove() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/artist/trends");
        setData(data);
      } catch (e) {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;
  }

  const d = data || {};
  const noData = !d.movers?.length && !d.top_tracks?.length;

  if (noData) {
    return (
      <EmptyState
        title="Not enough data yet"
        desc="Songs on the Move needs at least one royalty statement. Once your admin uploads a DSR, you'll see period-over-period trends here."
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="songs-on-the-move">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-xl tracking-tight text-white">Songs on the Move</h3>
          <p className="mt-1 text-xs text-slate-500">
            {d.current_period ? `${d.current_period}` : "Latest period"}
            {d.prior_period && <span> vs {d.prior_period}</span>}
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-blue-300 border border-blue-500/30 rounded-full px-3 py-1">
          <Rocket size={11} /> Trend signal
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Movers */}
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="movers-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-white font-medium">Biggest movers</div>
              <div className="text-xs text-slate-500">Ranked by percentage change vs. prior period</div>
            </div>
          </div>
          {(d.movers || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No movement in this period yet</div>
          ) : (
            <ul className="space-y-3">
              {(d.movers || []).map((m, i) => (
                <li key={(m.isrc || m.song) + i} data-testid={`mover-${i}`} className="flex items-center gap-4 rounded-xl border border-white/5 hover:border-white/15 bg-white/[0.02] px-4 py-3 transition-colors">
                  <span className="font-display text-lg text-white/30 w-6 text-right shrink-0">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-white truncate">{m.song || "Untitled"}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {m.artist || "—"}{m.album ? ` · ${m.album}` : ""}
                      {m.isrc && <span className="ml-2 text-slate-600">ISRC {m.isrc}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-white text-sm tabular-nums">{fmt(m.streams)}</div>
                    <div className="text-[11px] text-slate-500 tabular-nums">was {fmt(m.prior_streams)}</div>
                  </div>
                  <div className="shrink-0">
                    <ChangePill pct={m.pct_change} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Retailer breakdown */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="retailer-breakdown">
          <div className="mb-4">
            <div className="text-white font-medium">Streams by retailer</div>
            <div className="text-xs text-slate-500">Current period</div>
          </div>
          {(d.retailer_breakdown || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No retailer data yet</div>
          ) : (
            <ul className="space-y-2.5">
              {(d.retailer_breakdown || []).slice(0, 10).map((r) => {
                const max = d.retailer_breakdown[0].streams || 1;
                const w = Math.max(6, Math.round((r.streams / max) * 100));
                return (
                  <li key={r.retailer} className="text-sm">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-slate-300 truncate">{r.retailer}</span>
                      <span className="text-white tabular-nums">{fmt(r.streams)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400" style={{ width: `${w}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {(d.top_tracks || []).length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="top-tracks">
          <div className="mb-4">
            <div className="text-white font-medium">Top songs — {d.current_period || "current period"}</div>
            <div className="text-xs text-slate-500">Ranked by streams</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {d.top_tracks.slice(0, 8).map((t, i) => (
              <div key={(t.isrc || t.song) + i} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2">
                <span className="text-white/30 font-display text-sm w-5 text-right">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{t.song}</div>
                  <div className="text-[11px] text-slate-500 truncate">{t.artist || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-white text-sm tabular-nums">{fmt(t.streams)}</div>
                  <div className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                    ${Number(t.net || 0).toFixed(2)} <ArrowUpRight size={9} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
